// useFaxManagement.ts


import { useLazyQuery } from "@apollo/client/react";
import { useSnackbar } from 'notistack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GridCellParams, GridSortModel } from '@mui/x-data-grid';
import { SetStateAction, useEffect, useState } from 'react';
import { GET_FAX_DOWNLOAD_URL, GET_ADMIN_FAX_JOBS } from 'src/graphql/resolvers/queries';
import { FaxSearchQuery } from 'src/@types/shared.types';

// GraphQL queries

// Search query interface

// Enum for search fields
export enum FaxSearchField {
    status = 'status',
    providerName = 'providerName',
    clientName = 'clientName',
    startDate = 'startDate',
    endDate = 'endDate',
}

// Actions enum
export enum FaxListAction {
    VIEW_DETAILS = 'VIEW_DETAILS',
    DOWNLOAD = 'DOWNLOAD',
}

/**
 * Every URL search param this hook reads or writes (excluding the dynamic
 * `sort`/`sortN` keys, handled separately). Used to distinguish the hook's own
 * filter/pagination/sort state from unrelated host params that must be
 * preserved when the hook rewrites the URL.
 */
const FAX_PARAM_KEYS = new Set<string>([
    'status',
    'providerName',
    'clientName',
    'startDate',
    'endDate',
    'page',
    'pageSize',
]);

export function useFaxManagement(defaultPageSize: number) {
    // Hooks
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    // Search state
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: defaultPageSize,
    });

    const [sortModel, setSortModel] = useState<GridSortModel>([
        { field: 'createdAt', sort: 'desc' },
    ]);

    const handlePaginationChange = (model: SetStateAction<{ page: number; pageSize: number }>) => {
        setPaginationModel(model);
    };

    const handleSortModelChange = (model: GridSortModel) => {
        setSortModel(model);
    };

    // Fax jobs data state
    const [listedFaxJobs, setListedFaxJobs] = useState<{
        faxJobs: any[];
        totalCount: number;
    } | null>(null);

    // Main search query
    const [
        searchFaxJobs,
        { data: faxJobsData, error: faxJobsError, loading: faxJobsLoading },
    ] = useLazyQuery(GET_ADMIN_FAX_JOBS, {
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        if (faxJobsData) {
            setListedFaxJobs({
                faxJobs: faxJobsData.adminFaxJobs.faxJobs,
                totalCount: faxJobsData.adminFaxJobs.totalCount,
            });
        }
    }, [faxJobsData]);

    useEffect(() => {
        if (faxJobsError) {
            enqueueSnackbar(`Error loading fax jobs: ${faxJobsError.message}`, {
                variant: 'error',
            });
        }
    }, [faxJobsError]);

    // Download URL query
    const [getDownloadUrl, { data: downloadData, error: downloadError, loading: downloadLoading }] =
        useLazyQuery(GET_FAX_DOWNLOAD_URL);

    useEffect(() => {
        if (downloadData?.getFaxDocumentDownloadUrl) {
            window.open(downloadData.getFaxDocumentDownloadUrl, '_blank');
        }
    }, [downloadData]);

    useEffect(() => {
        if (downloadError) {
            enqueueSnackbar(`Error downloading fax: ${downloadError.message}`, {
                variant: 'error',
            });
        }
    }, [downloadError]);

    // Search params
    let [searchParams, setSearchParams] = useSearchParams();
    let [superQuery, setSuperQuery] = useState<FaxSearchQuery | null>(null);

    const search = () => {
        if (!superQuery) return;

        // Build URL search params
        const params = Object.keys(superQuery).reduce((acc, key) => {
            if (superQuery && superQuery[key as FaxSearchField] != null) {
                acc.set(key, String(superQuery[key as FaxSearchField]));
            }
            return acc;
        }, new URLSearchParams());

        // Pagination
        params.set('page', String(paginationModel.page));
        params.set('pageSize', String(paginationModel.pageSize));

        // Sort
        sortModel.forEach((sort, index) => {
            params.set(`sort${index > 0 ? index : ''}`, `${sort.field},${sort.sort}`);
        });

        // Carry over any param this hook doesn't manage so rewriting the filter
        // state doesn't drop unrelated host params. Managed keys (and the
        // dynamic `sort`/`sortN` keys) are excluded so cleared filters don't get
        // resurrected from the previous URL.
        searchParams.forEach((value, key) => {
            if (!FAX_PARAM_KEYS.has(key) && !/^sort\d*$/.test(key) && !params.has(key)) {
                params.append(key, value);
            }
        });

        setSearchParams(params);

        // Build filter for GraphQL query
        const filter: any = {};
        if (superQuery.status) filter.status = superQuery.status;
        if (superQuery.providerName) filter.providerNameLike = superQuery.providerName;
        if (superQuery.clientName) filter.clientNameLike = superQuery.clientName;
        if (superQuery.startDate) filter.startDate = superQuery.startDate;
        if (superQuery.endDate) filter.endDate = superQuery.endDate;

        searchFaxJobs({
            variables: {
                offset: paginationModel.page * paginationModel.pageSize,
                limit: paginationModel.pageSize,
                filter,
                sort: sortModel.map((sort) => ({
                    field: sort.field,
                    order: sort.sort?.toUpperCase(),
                })),
            },
        });
    };

    // Trigger search when dependencies change
    useEffect(() => {
        search();
    }, [superQuery, paginationModel, sortModel]);

    // Parse URL params on mount
    useEffect(() => {
        const parsedQuery: FaxSearchQuery = {
            status: searchParams.get('status') || undefined,
            providerName: searchParams.get('providerName') || undefined,
            clientName: searchParams.get('clientName') || undefined,
            startDate: searchParams.get('startDate') || undefined,
            endDate: searchParams.get('endDate') || undefined,
        };

        // Parse pagination
        const page = searchParams.get('page') ? parseInt(searchParams.get('page') || '0') : 0;
        const pageSize = searchParams.get('pageSize')
            ? parseInt(searchParams.get('pageSize') || String(defaultPageSize))
            : defaultPageSize;

        setPaginationModel({ page, pageSize });

        // Parse sort
        const sort = searchParams.get('sort');
        if (sort) {
            const [field, sortDirection] = sort.split(',');
            setSortModel([{ field, sort: sortDirection as any }]);
        }

        setSuperQuery(parsedQuery);
    }, []);

    // Handlers
    const handleCellClick = (params: GridCellParams) => {
        const { field, row } = params;
        if (field === 'actions') {
            return;
        }
        // Open fax details modal
        setAction({
            type: FaxListAction.VIEW_DETAILS,
            faxJob: row,
        });
    };

    const handleSearch = (query: FaxSearchQuery) => {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        setSuperQuery(query);
    };

    const handleDownloadFax = (faxJobId: string) => {
        getDownloadUrl({
            variables: { faxJobId },
        });
    };

    // Action state for modals/dialogs
    const [action, setAction] = useState<{
        type: FaxListAction;
        faxJob: any | null;
    } | null>(null);

    // Modal handlers
    const viewFaxDetails = (fax: any) => {
        setAction({
            type: FaxListAction.VIEW_DETAILS,
            faxJob: fax,
        });
    };

    const closeModal = () => {
        setAction(null);
    };

    return {
        // Search & handlers
        superQuery,
        handleSearch,
        listedFaxJobs,
        faxJobsLoading,
        faxJobsError,
        paginationModel,
        handlePaginationChange,
        sortModel,
        handleSortModelChange,
        handleCellClick,

        // Download functionality
        handleDownloadFax,
        downloadLoading,

        // Modal/Action state
        action,
        setAction,
        viewFaxDetails,
        closeModal,
    };
}
